<script setup>
import { computed, ref } from 'vue'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { useBillingStore } from '@/stores/billing'
import ProfileAccountBillingService from '@/services/profile-account-billing.service'
import { errorMessage } from '@/utils/notifications'
import {
    intervalName,
    priceLabel,
    usagePercent,
    formatMoney,
} from '@/helpers/billing-format'

/**
 * The plan the account is on: what it is and costs, how much of it has been
 * used, when it renews or the trial ends, any change that is scheduled (with a
 * one-click Undo) and the ways to change it.
 *
 * Every action leads to the same three steps on the plan page (choose, review,
 * confirm), so the buttons only link there. `intent` tells that page what the
 * owner came to do.
 */
const billingStore = useBillingStore()
const { can } = usePermissions()

const summary = computed(() => billingStore.summary)
const subscription = computed(() => summary.value?.subscription ?? null)
const plan = computed(() => subscription.value?.plan ?? null)
const scheduledChange = computed(() => summary.value?.scheduled_change ?? null)
const macs = computed(() => summary.value?.entitlements?.macs ?? null)
const trial = computed(() => subscription.value?.trial ?? null)
const status = computed(() => subscription.value?.status ?? null)
const canManage = computed(() => can(PERMISSIONS.BILLING_UPDATE))

const STATE_LABELS = {
    trialing: 'Trial',
    free: 'Free',
    active: 'Active',
    past_due: 'Past due',
    suspended: 'Suspended',
    cancelled: 'Cancelled',
}

const stateLabel = computed(() => STATE_LABELS[status.value] ?? status.value)
const stateClass = computed(() => {
    switch (status.value) {
        case 'past_due':
        case 'suspended':
            return 'bg-red-100 text-red-800'
        case 'cancelled':
            return 'bg-taupe-200 text-taupe-700'
        case 'trialing':
            return 'bg-sky-100 text-sky-800'
        default:
            return 'bg-emerald-100 text-emerald-800'
    }
})

const isFree = computed(() => status.value === 'free' || !!plan.value?.is_free)
const isTrial = computed(() => status.value === 'trialing')

// Dates are billing dates, not moments: render the date as the API sent it.
const dateLine = computed(() => {
    if (isTrial.value && trial.value) {
        return { label: 'Trial ends', value: trial.value.ends_at }
    }
    if (status.value === 'active' || status.value === 'past_due') {
        return scheduledChange.value?.kind === 'cancel'
            ? null
            : { label: 'Renews', value: subscription.value.current_period_end }
    }
    return null
})

const SCHEDULED_KINDS_WITH_UNDO = ['downgrade', 'interval_switch', 'cancel']

const canUndo = computed(
    () =>
        canManage.value &&
        SCHEDULED_KINDS_WITH_UNDO.includes(scheduledChange.value?.kind)
)

const meter = computed(() => {
    if (!macs.value) return null

    const percent = usagePercent(macs.value.usage, macs.value.limit)
    let barClass = 'bg-gradient-to-r from-sky-500 to-blue-500'
    if (macs.value.over_limit || percent === 100) {
        barClass = 'bg-red-500'
    } else if (percent !== null && percent >= 80) {
        barClass = 'bg-amber-500'
    }

    return { percent, barClass }
})

const dispute = computed(() => !!subscription.value?.collection_paused)

// Which actions apply to the state the subscription is in. A suspended account
// gets its way back from the status alert (a working card), and a cancelled
// one has no plan to change yet.
const actions = computed(() => {
    if (!canManage.value || dispute.value) return []
    if (scheduledChange.value?.kind === 'cancel' && !isTrial.value) {
        return []
    }

    switch (status.value) {
        case 'active':
        case 'past_due':
            return [
                { key: 'change', label: 'Change plan', variant: 'primary' },
                {
                    key: 'interval',
                    label:
                        plan.value?.interval === 'year'
                            ? 'Switch to monthly'
                            : 'Switch to annual',
                    variant: 'secondary',
                },
                { key: 'cancel', label: 'Cancel plan', variant: 'link-danger' },
            ]
        case 'trialing':
            return [
                {
                    key: 'change',
                    label: 'Choose what happens when your trial ends',
                    variant: 'primary',
                },
                { key: 'cancel', label: 'Cancel plan', variant: 'link-danger' },
            ]
        case 'free':
            return [{ key: 'change', label: 'Upgrade', variant: 'primary' }]
        case 'cancelled':
            return [{ key: 'change', label: 'Reactivate', variant: 'primary' }]
        default:
            return []
    }
})

function routeFor(action) {
    return { name: 'AdminBillingPlansScreen', query: { intent: action.key } }
}

const undoing = ref(false)

async function undo() {
    undoing.value = true
    try {
        const response =
            await ProfileAccountBillingService.undoScheduledChange()
        await billingStore.applySummary(response.data)
    } catch (error) {
        errorMessage(
            error?.response?.data?.message ??
                "We couldn't undo that change. Try again."
        )
    } finally {
        undoing.value = false
    }
}

const scheduledTitle = computed(() => {
    const change = scheduledChange.value
    if (!change) return ''

    switch (change.kind) {
        case 'downgrade':
            return `Moves to ${change.target?.plan_name ?? 'a smaller plan'}`
        case 'interval_switch':
            return `Switches to ${intervalName(change.target?.interval)} billing`
        case 'cancel':
            return isTrial.value
                ? 'You move to Free when your trial ends'
                : 'Cancels, and you move to Free'
        case 'plan_migration':
            return `Updates to a new version of ${plan.value?.plan_name ?? 'your plan'}`
        default:
            return 'Plan change scheduled'
    }
})
</script>

<template>
    <section
        data-testid="plan-card"
        aria-labelledby="plan-card-heading"
        class="flex h-full flex-col rounded-xl border border-taupe-200 bg-white p-6 shadow-sm"
    >
        <h2 id="plan-card-heading" class="sr-only">Your plan</h2>

        <template v-if="plan">
            <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                        <span
                            data-testid="plan-name"
                            class="text-2xl font-semibold text-taupe-900"
                            >{{ plan.plan_name }}</span
                        >
                        <span
                            data-testid="plan-state"
                            class="rounded-full px-2.5 py-0.5 text-xs font-medium"
                            :class="stateClass"
                            >{{ stateLabel }}</span
                        >
                    </div>
                    <p
                        data-testid="plan-price"
                        class="mt-1 text-sm text-taupe-600"
                    >
                        {{ priceLabel(plan) }}
                        <template v-if="!isFree">
                            · billed {{ intervalName(plan.interval) }}</template
                        >
                    </p>
                </div>
            </div>

            <div v-if="meter" class="mt-6 space-y-2" data-testid="plan-usage">
                <div class="flex items-center justify-between">
                    <span class="text-sm font-medium text-taupe-700"
                        >Monthly active contexts</span
                    >
                    <span class="text-sm font-medium text-taupe-900">
                        {{ macs.usage.toLocaleString() }}
                        <template v-if="macs.limit !== null">
                            of {{ macs.limit.toLocaleString() }}
                            <template v-if="meter.percent !== null"
                                >({{ meter.percent }}%)</template
                            >
                        </template>
                        <template v-else> · no limit</template>
                    </span>
                </div>
                <div
                    v-if="meter.percent !== null"
                    role="progressbar"
                    aria-label="Monthly active contexts used"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    :aria-valuenow="meter.percent"
                    class="h-2 w-full overflow-hidden rounded-full bg-taupe-200"
                >
                    <div
                        data-testid="plan-usage-bar"
                        class="h-2 rounded-full transition-all duration-300"
                        :class="meter.barClass"
                        :style="{ width: meter.percent + '%' }"
                    ></div>
                </div>
                <p
                    v-if="macs.over_limit"
                    class="text-sm text-red-700"
                    data-testid="plan-usage-over"
                >
                    You're over your plan's limit.
                </p>
            </div>

            <p
                v-if="dateLine"
                data-testid="plan-date"
                class="mt-6 text-sm text-taupe-600"
            >
                {{ dateLine.label }}
                <span class="font-medium text-taupe-900"
                    ><zen-date-time :value="dateLine.value" format="date" utc
                /></span>
            </p>

            <div
                v-if="scheduledChange"
                data-testid="plan-scheduled-change"
                class="mt-6 flex items-start justify-between gap-3 rounded-lg border border-sky-200 bg-sky-50 p-4"
            >
                <div class="min-w-0 text-sm text-sky-900">
                    <div class="font-medium">{{ scheduledTitle }}</div>
                    <div class="mt-0.5 text-sky-800">
                        Takes effect on
                        <zen-date-time
                            :value="scheduledChange.effective_at"
                            format="date"
                            utc
                        />.
                    </div>
                </div>
                <zen-button
                    v-if="canUndo"
                    variant="secondary"
                    size="small"
                    :processing="undoing"
                    processing-label="Undoing"
                    data-testid="plan-undo"
                    @click="undo"
                >
                    Undo
                </zen-button>
            </div>

            <p
                v-if="dispute"
                data-testid="plan-on-hold"
                class="mt-6 rounded-lg bg-amber-50 p-3 text-sm text-amber-800"
            >
                Plan changes are on hold while a payment dispute is open.
            </p>

            <p
                v-if="summary.credit_balance?.amount > 0"
                data-testid="plan-credit"
                class="mt-6 text-sm text-taupe-600"
            >
                You have
                <span class="font-medium text-taupe-900">{{
                    formatMoney(
                        summary.credit_balance.amount,
                        summary.credit_balance.currency
                    )
                }}</span>
                in credit. It comes off your next invoices until it runs out.
            </p>

            <div
                v-if="actions.length"
                class="mt-auto flex flex-wrap items-center gap-3 pt-6"
                data-testid="plan-actions"
            >
                <zen-button
                    v-for="action in actions"
                    :key="action.key"
                    :to="routeFor(action)"
                    :variant="action.variant"
                    :data-testid="`plan-action-${action.key}`"
                >
                    {{ action.label }}
                </zen-button>
            </div>
        </template>

        <p v-else class="text-sm text-taupe-600" data-testid="plan-empty">
            We couldn't find a plan for your account.
        </p>
    </section>
</template>
